import React, { useState } from 'react';
import { createContactMessage } from '../lib/api';
import { businessInfo } from '../config/businessInfo';

const faqs = [
  {
    question: 'What are your shop hours?',
    answer: 'Our Asaba shop is open Monday to Saturday, 10 AM to 8 PM, and Sunday, 12 PM to 6 PM.',
  },
  {
    question: 'How far in advance should I book catering?',
    answer: 'Two weeks is best for most events. For larger or custom events, three to four weeks gives the team more room to plan.',
  },
  {
    question: 'What areas do you deliver to?',
    answer: 'We deliver across Asaba and nearby areas. Event logistics outside the core area are coordinated case by case.',
  },
  {
    question: 'Can I rent equipment for my own event?',
    answer: 'Yes. Use the rentals page to browse equipment, then contact our team to confirm availability and logistics.',
  },
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: 'catering',
    eventDate: '',
    guestCount: '',
    message: '',
  });
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ tone: 'idle', message: '' });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      await createContactMessage({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        serviceType: formData.serviceType,
        eventDate: formData.eventDate,
        guestCount: formData.guestCount,
        message: formData.message,
      });

      setSubmitStatus({ tone: 'success', message: "Thank you. We'll get back to you within 24 hours." });
      setFormData({
        name: '',
        email: '',
        phone: '',
        serviceType: 'catering',
        eventDate: '',
        guestCount: '',
        message: '',
      });
    } catch (error) {
      setSubmitStatus({ tone: 'error', message: error.message || 'Unable to send message right now. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  return (
    <main className="contact-page">
      <section className="contact-hero">
        <div className="contact-hero-content">
          <p className="eyebrow">Contact</p>
          <h1>Talk to Vemmac Kitchen</h1>
          <p>
            Send us a message for catering, rentals, shop orders, complaints, or general support.
            We keep responses clear so you know the next step.
          </p>
        </div>
      </section>

      <section className="contact-info-section">
        <h2>How to reach us</h2>
        <div className="contact-methods">
          <div className="contact-method">
            <span className="method-icon">Phone</span>
            <h3>Call us</h3>
            <p>Speak with the shop or leave a message.</p>
            <a href={businessInfo.phoneHref} className="contact-link">{businessInfo.phoneDisplay}</a>
          </div>
          <div className="contact-method">
            <span className="method-icon">Email</span>
            <h3>Email</h3>
            <p>Send event, rental, or order details.</p>
            <a href={businessInfo.emailHref} className="contact-link">{businessInfo.email}</a>
          </div>
          <div className="contact-method">
            <span className="method-icon">Visit</span>
            <h3>Visit our shop</h3>
            <p>{businessInfo.addressLines.join(', ')}</p>
          </div>
          <div className="contact-method">
            <span className="method-icon">Hours</span>
            <h3>Shop hours</h3>
            <p>Mon-Sat: 10 AM-8 PM</p>
            <p>Sunday: 12 PM-6 PM</p>
          </div>
        </div>
      </section>

      <section className="contact-form-section">
        <div className="contact-form-header">
          <h2>Send us a message</h2>
          <p>Tell us what you need and the team will follow up.</p>
        </div>
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Full name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+234 800 000 0000"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="serviceType">Service type *</label>
              <select
                id="serviceType"
                name="serviceType"
                value={formData.serviceType}
                onChange={handleChange}
                required
              >
                <option value="catering">Catering Services</option>
                <option value="rentals">Equipment Rentals</option>
                <option value="menu">Menu Ordering</option>
                <option value="complaints">Order complaints</option>
                <option value="other">Other inquiry</option>
              </select>
            </div>
            {(formData.serviceType === 'catering' || formData.serviceType === 'rentals') && (
              <>
                <div className="form-group">
                  <label htmlFor="eventDate">Event date</label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="guestCount">Guest count / quantity</label>
                  <input
                    type="number"
                    min="1"
                    id="guestCount"
                    name="guestCount"
                    value={formData.guestCount}
                    onChange={handleChange}
                    placeholder="Estimated guests or pieces"
                  />
                </div>
              </>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="message">Message *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us what you need."
              rows="6"
              required
            />
          </div>

          {submitStatus.message && (
            <p className={`form-status form-status--${submitStatus.tone}`}>{submitStatus.message}</p>
          )}

          <button type="submit" className="form-submit-btn" disabled={submitting}>
            {submitting ? 'Sending...' : 'Send message'}
          </button>
        </form>
      </section>

      <section className="contact-faq-section">
        <div className="faq-header">
          <h2>Frequently asked questions</h2>
          <p>Quick answers for ordering, catering, delivery, and rentals.</p>
        </div>
        <div className="faq-accordion">
          {faqs.map((faq, index) => (
            <div key={faq.question} className="accordion-item">
              <button
                type="button"
                className="accordion-trigger"
                onClick={() => toggleAccordion(index)}
                aria-expanded={activeAccordion === index}
              >
                <span>{faq.question}</span>
                <span className="accordion-icon">+</span>
              </button>
              {activeAccordion === index && (
                <div className="accordion-content">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
